// Domain types for the Smart Links portal.
// Mirrors the POST /links contract in the meta repo's
// docs/integration-design.md §3 Seam 1 and the analytics event classes in §7.1.
// RULE-01: customer_id is NEVER part of any client-sent payload — the backend
// resolves it from x-api-key. It only appears on records the server returns.

export const CHANNELS = ['SMS', 'WHATSAPP', 'EMAIL', 'VOICE', 'PUSH'] as const;
export type Channel = (typeof CHANNELS)[number];

/** What the operator intends this link to do — drives the fields shown at mint time. */
export const PURPOSES = ['website', 'app_download', 'deep_link', 'whatsapp'] as const;
export type Purpose = (typeof PURPOSES)[number];

export const PURPOSE_LABELS: Record<Purpose, string> = {
  website: 'Website',
  app_download: 'App Download Page',
  deep_link: 'Deep Link',
  whatsapp: 'WhatsApp Number',
};

export const PLATFORMS = ['ios', 'android', 'web', 'in-app-browser'] as const;
export type Platform = (typeof PLATFORMS)[number];

export const LINK_STATUSES = ['active', 'expired', 'disabled'] as const;
export type LinkStatus = (typeof LINK_STATUSES)[number];

/** Key/value bag used for deep-link params and UTM params. */
export type StringMap = Record<string, string>;

/** A short link as returned by the API. */
export interface Link {
  id: string;
  /** Human-readable name set by the operator. */
  linkName: string;
  shortCode: string;
  shortUrl: string;
  /** What the operator picked at mint time; drives redirect behaviour. */
  purpose: Purpose;
  /** 'referral' for a referral template / minted per-person link; else 'standard'. */
  kind?: 'standard' | 'referral';
  /** The sharer's reference, set on a minted per-person referral link. */
  referrerId?: string;
  /** Resolved primary destination — set per purpose (see CreateLinkInput). */
  destination: string;
  /** Legacy channel tag; the new UI no longer asks for it. */
  channel: Channel;
  campaignId?: string;
  campaignName?: string;
  /** Subscriber reference — never raw PII (RULE-02). */
  contactRef?: string;
  /** Website purpose only. */
  websiteUrl?: string;
  /** App Download + Deep-Link fallback. */
  iosAppStoreUrl?: string;
  androidAppStoreUrl?: string;
  ipadAppStoreUrl?: string;
  huaweiAppGalleryUrl?: string;
  webFallbackUrl?: string;
  /** Deep Link purpose. */
  androidDeepLink?: string;
  iosDeepLink?: string;
  /** Social-breakout config: Android package (intent) + app scheme (iOS). */
  androidPackageName?: string;
  /** Legacy explicit fields (still surfaced in detail view if set). */
  appScheme?: string;
  iosUniversalLink?: string;
  androidAppLink?: string;
  deepLinkPath?: string;
  deepLinkParameters?: StringMap;
  utmParameters?: StringMap;
  attributionWindowHours: number;
  status: LinkStatus;
  isActive: boolean;
  /** null when the operator picked "Never expire". */
  expiresAt?: string | null;
  createdAt: string;
  updatedAt: string;
  /** Rolled-up counters (server-aggregated from xnotify.events). */
  metrics: LinkMetricsSummary;
}

/** Lightweight counters shown in the list view. */
export interface LinkMetricsSummary {
  clicks: number;
  installs: number;
  opens: number;
  /** installs / clicks, 0..1. */
  installRate: number;
}

/** Payload for POST /links. No customer_id (RULE-01). */
export interface CreateLinkInput {
  /** Operator-provided human label. */
  linkName: string;
  /** Client-suggested 6-char slug shown to the user at mint time. */
  slug: string;
  purpose: Purpose;
  /** 'referral' marks a referral link; else 'standard'. */
  kind?: 'standard' | 'referral';
  /** Referral code = the sharer's id every install through the link is credited to. */
  referrerId?: string;
  /** Server-resolved primary destination — supplied by the client based on purpose. */
  destination: string;
  channel: Channel;
  campaignId?: string;
  campaignName?: string;
  contactRef?: string;
  websiteUrl?: string;
  iosAppStoreUrl?: string;
  androidAppStoreUrl?: string;
  ipadAppStoreUrl?: string;
  huaweiAppGalleryUrl?: string;
  webFallbackUrl?: string;
  androidDeepLink?: string;
  iosDeepLink?: string;
  /** Social-breakout config: Android package (intent) + app scheme (iOS). */
  androidPackageName?: string;
  appScheme?: string;
  iosUniversalLink?: string;
  androidAppLink?: string;
  deepLinkPath?: string;
  deepLinkParameters?: StringMap;
  utmParameters?: StringMap;
  expiresAt?: string | null;
  attributionWindowHours: number;
  /** WhatsApp purpose: click-to-chat number (+ optional prefilled message). */
  whatsappNumber?: string;
  whatsappMessage?: string;
}

/** Tenant-level app identity — set once, merged into every app link at redirect time. */
export interface AppProfile {
  androidPackageName?: string;
  appScheme?: string;
  iosBundleId?: string;
  androidAppStoreUrl?: string;
  iosAppStoreUrl?: string;
  ipadAppStoreUrl?: string;
  huaweiAppGalleryUrl?: string;
  webFallbackUrl?: string;
}

export interface ListLinksParams {
  search?: string;
  status?: LinkStatus | 'all';
  channel?: Channel | 'all';
  campaignId?: string;
  page: number;
  pageSize: number;
  sortBy?: 'createdAt' | 'clicks' | 'installs';
  sortDir?: 'asc' | 'desc';
}

export interface ListLinksResult {
  items: Link[];
  total: number;
  page: number;
  pageSize: number;
}

/** A single point on the clicks/installs time series. */
export interface TimeseriesPoint {
  /** ISO date (day granularity). */
  date: string;
  clicks: number;
  installs: number;
  opens: number;
}

export interface BreakdownSlice {
  label: string;
  value: number;
}

export interface LinkAnalytics {
  linkId: string;
  shortCode: string;
  windowDays: number;
  totals: {
    clicks: number;
    installs: number;
    opens: number;
    /** distinct devices (HyperLogLog over the click fingerprint), approx. */
    uniqueDevices?: number;
    installRate: number;
    /** clicks that led to a store visit / scheme attempt, 0..1. */
    ctr: number;
  };
  timeseries: TimeseriesPoint[];
  byPlatform: BreakdownSlice[];
  byChannel: BreakdownSlice[];
  /** Clicks grouped by visitor country (ISO-3166 alpha-2), desc. */
  byCountry?: BreakdownSlice[];
  /** Clicks grouped by visitor city, desc. */
  byCity?: BreakdownSlice[];
  /** Clicks grouped by region as "CC|Region name" (e.g. "PK|Punjab"), desc. */
  byRegion?: BreakdownSlice[];
  /** Clicks grouped by device model (e.g. "Pixel 7"; Android-reliable), desc. */
  byDeviceModel?: BreakdownSlice[];
  /** Clicks grouped by OS (e.g. "Android", "iOS"), desc. */
  byOS?: BreakdownSlice[];
  /** Clicks grouped by OS + version (e.g. "Android 14"), desc. */
  byOSVersion?: BreakdownSlice[];
  recentEvents: AnalyticsEvent[];
}

export interface AnalyticsEvent {
  id: string;
  /** Present on the global activity feed — which link the event belongs to. */
  linkId?: string;
  eventName: 'link_clicked' | 'app_installed' | 'app_first_open' | 'app_opened';
  platform: Platform;
  channel?: string;
  country?: string;
  region?: string;
  city?: string;
  device?: string;
  os?: string;
  referrer?: string;
  isBot?: boolean;
  inAppBrowser?: boolean;
  messageId?: string;
  /** ISO timestamp. */
  at: string;
  properties?: { target?: string };
}

export interface ActivityFeed {
  events: AnalyticsEvent[];
  range?: { from?: string; to?: string; limit?: number };
}
